import * as React from "react";
import { useMutation } from "convex/react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ComboboxSelect,
  type ComboboxOption,
} from "@/components/ui/combobox-select";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/convexApi";
import { formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";
import { parseAmountInput, roundToCents } from "../bill-shared";

export interface AciAllocationRowData {
  _id: string;
  cargoId?: string;
  aciCode?: string;
  memo: string;
  amount: number;
  position: number;
}

export interface AciOption {
  cargoId: string;
  aciCode: string;
  productName?: string;
  status?: string;
}

/**
 * UI-only sentinel for the "Overhead Expense" bucket; persisted as a row with
 * no cargoId, never as this string.
 */
const OVERHEAD_VALUE = "__overhead__";
const OVERHEAD_LABEL = "Overhead Expense";

interface EditableRow {
  key: number;
  target: string; // "" (unset) | OVERHEAD_VALUE | cargoId
  memo: string;
  amount: string;
}

let nextRowKey = 1;

function toEditableRows(allocations: AciAllocationRowData[]): EditableRow[] {
  return allocations.map((allocation) => ({
    key: nextRowKey++,
    target: allocation.cargoId ?? OVERHEAD_VALUE,
    memo: allocation.memo,
    amount: String(allocation.amount),
  }));
}

/**
 * Content signature used for dirty-detection: compares what the rows *mean*
 * (amounts numerically normalized) so "5" and "5.00" are the same edit state.
 */
function rowsSignature(rows: EditableRow[]): string {
  return rows
    .map(
      (row) =>
        `${row.target}|${row.memo}|${parseAmountInput(row.amount) ?? row.amount}`,
    )
    .join("\n");
}

/**
 * ACI cost coding — attributes bill amounts to coffee purchases (ACI numbers)
 * in parallel with the GL coding. Optional: an uncoded bill submits fine, but
 * once rows exist they must cover the bill total.
 */
export function AciAllocationsEditor({
  authUserId,
  billId,
  billRevision,
  allocations,
  aciOptions,
  totalAmount,
  canEdit,
  onDirtyChange,
}: {
  authUserId: string | undefined;
  billId: string;
  billRevision: number;
  allocations: AciAllocationRowData[];
  aciOptions: AciOption[] | undefined;
  totalAmount: number | undefined;
  canEdit: boolean;
  /** Reports unsaved-edit state so the page can guard queue navigation. */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const setAciAllocations = useMutation(api.accountingBillAci.setAciAllocations);
  const [rows, setRows] = React.useState<EditableRow[]>(() =>
    toEditableRows(allocations),
  );
  const [isSaving, setIsSaving] = React.useState(false);
  const [invalidKeys, setInvalidKeys] = React.useState<Set<number>>(
    () => new Set(),
  );
  const [conflict, setConflict] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const seedSignatureRef = React.useRef(
    rowsSignature(toEditableRows(allocations)),
  );
  const seededBillIdRef = React.useRef(billId);

  const dirty = rowsSignature(rows) !== seedSignatureRef.current;
  const dirtyRef = React.useRef(dirty);
  dirtyRef.current = dirty;

  // A non-editable (locked) bill never reports dirty: its read-only view has
  // already discarded local edits, so they must not block queue navigation.
  React.useEffect(() => {
    onDirtyChange?.(canEdit && dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, canEdit, onDirtyChange]);

  const applySeed = React.useCallback(
    (serverAllocations: AciAllocationRowData[]) => {
      const seeded = toEditableRows(serverAllocations);
      setRows(seeded);
      seedSignatureRef.current = rowsSignature(seeded);
      setInvalidKeys(new Set());
      setConflict(false);
    },
    [],
  );

  // Re-seed when the server revision moves — but never clobber unsaved edits:
  // with local edits in flight we surface a conflict notice instead.
  React.useEffect(() => {
    if (seededBillIdRef.current !== billId) {
      seededBillIdRef.current = billId;
      applySeed(allocations);
      return;
    }
    if (!dirtyRef.current) {
      applySeed(allocations);
    } else {
      setConflict(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billId, billRevision]);

  const updateRow = (key: number, patch: Partial<EditableRow>) => {
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
    setInvalidKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const sum = roundToCents(
    rows.reduce((total, row) => total + (parseAmountInput(row.amount) ?? 0), 0),
  );
  const delta =
    totalAmount !== undefined ? roundToCents(sum - totalAmount) : undefined;
  const balanced = delta !== undefined && Math.abs(delta) < 0.005;

  const addRow = React.useCallback(() => {
    const key = nextRowKey++;
    setRows((prev) => {
      // Prefill the uncoded remainder so the common single-split and
      // last-line cases need no typing (Excel-style, like GL rows).
      const currentSum = roundToCents(
        prev.reduce(
          (total, row) => total + (parseAmountInput(row.amount) ?? 0),
          0,
        ),
      );
      const remainder =
        totalAmount !== undefined
          ? roundToCents(totalAmount - currentSum)
          : undefined;
      return [
        ...prev,
        {
          key,
          target: "",
          memo: "",
          amount:
            remainder !== undefined && remainder > 0 ? String(remainder) : "",
        },
      ];
    });
    requestAnimationFrame(() => {
      containerRef.current
        ?.querySelector<HTMLButtonElement>(
          `[data-aci-row="${key}"] button[role="combobox"]`,
        )
        ?.focus();
    });
  }, [totalAmount]);

  const handleSave = async () => {
    if (!authUserId) return;
    // Validate every row at once — highlight all problems, not just the first.
    const invalid = new Set<number>();
    for (const row of rows) {
      const amount = parseAmountInput(row.amount);
      if (!row.target || amount === undefined || amount <= 0) {
        invalid.add(row.key);
      }
    }
    if (invalid.size > 0) {
      setInvalidKeys(invalid);
      toast.error(
        `Fix the ${invalid.size} highlighted row${invalid.size === 1 ? "" : "s"} — every ACI line needs an ACI (or Overhead) and a positive amount`,
      );
      return;
    }
    const payload = rows.map((row) => ({
      cargoId: row.target === OVERHEAD_VALUE ? undefined : row.target,
      memo: row.memo.trim(),
      amount: parseAmountInput(row.amount)!,
    }));
    setIsSaving(true);
    try {
      await setAciAllocations({ authUserId, billId, allocations: payload });
      // Our rows now ARE the server state; the next revision-driven re-seed
      // should not read as a conflict.
      seedSignatureRef.current = rowsSignature(rows);
      setConflict(false);
      toast.success("ACI coding saved");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not save ACI coding",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const targetOptions = React.useMemo<ComboboxOption[]>(
    () => [
      { value: OVERHEAD_VALUE, label: OVERHEAD_LABEL },
      ...(aciOptions ?? []).map((option) => ({
        value: option.cargoId,
        label: `ACI ${option.aciCode}${option.productName ? ` — ${option.productName}` : ""}`,
      })),
    ],
    [aciOptions],
  );
  const targetLabelByValue = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const option of targetOptions) map.set(option.value, option.label);
    return map;
  }, [targetOptions]);

  const uncoded =
    totalAmount !== undefined &&
    (canEdit ? rows.length === 0 : allocations.length === 0);
  const heading = (
    <span className="flex items-center gap-2">
      <h3 className="text-sm font-semibold">ACI cost coding</h3>
      {uncoded && (
        <Badge variant="secondary" className="text-muted-foreground">
          Uncoded
        </Badge>
      )}
    </span>
  );

  if (!canEdit) {
    return (
      <div className="space-y-2">
        {heading}
        {allocations.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No ACI coding — costs on this bill are not attributed to a coffee
            purchase.
          </p>
        ) : (
          <div className="space-y-1 text-sm">
            {allocations.map((allocation) => (
              <div
                key={allocation._id}
                className="flex items-center justify-between gap-2 rounded border px-2 py-1"
              >
                <span className="min-w-0 truncate">
                  {allocation.cargoId
                    ? `ACI ${allocation.aciCode ?? "(unknown)"}`
                    : OVERHEAD_LABEL}
                  {allocation.memo ? (
                    <span className="text-muted-foreground">
                      {" "}
                      — {allocation.memo}
                    </span>
                  ) : null}
                </span>
                <span className="tabular-nums">
                  {formatCurrency(allocation.amount)}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between px-2 pt-1 text-xs">
              <span className="text-muted-foreground">Coded</span>
              <span className="tabular-nums">
                {formatCurrency(
                  roundToCents(
                    allocations.reduce((total, a) => total + a.amount, 0),
                  ),
                )}
                {totalAmount !== undefined && ` of ${formatCurrency(totalAmount)}`}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2" ref={containerRef}>
      <div className="flex items-center justify-between">
        {heading}
        <Button variant="ghost" size="sm" onClick={addRow}>
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add row
        </Button>
      </div>

      {conflict && dirty && (
        <div className="rounded-md border px-2.5 py-1.5 text-xs">
          <span className="font-medium">
            The ACI coding changed elsewhere while you were editing.
          </span>{" "}
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={() => applySeed(allocations)}
          >
            Load the latest (discards your edits)
          </button>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          Optional — code this bill to the coffee purchases (ACI numbers) it
          belongs to, or to Overhead Expense. Once rows exist they must cover
          the bill total.
        </p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((row, index) => {
            const isLast = index === rows.length - 1;
            const isInvalid = invalidKeys.has(row.key);
            return (
              <div
                key={row.key}
                data-aci-row={row.key}
                className={cn(
                  "flex items-center gap-1.5 rounded",
                  isInvalid && "ring-destructive/60 rounded-md p-0.5 ring-1",
                )}
              >
                <ComboboxSelect
                  options={targetOptions}
                  value={row.target}
                  placeholder="ACI / Overhead"
                  searchPlaceholder="Type to filter ACIs…"
                  emptyText="No matching ACI."
                  loading={aciOptions === undefined}
                  className="h-8 w-56 text-xs font-normal"
                  onValueChange={(value) => updateRow(row.key, { target: value })}
                />
                <Input
                  value={row.memo}
                  onChange={(e) => updateRow(row.key, { memo: e.target.value })}
                  placeholder="Memo"
                  aria-label="ACI allocation memo"
                  className="h-8 flex-1 text-xs"
                />
                <Input
                  value={row.amount}
                  onChange={(e) => updateRow(row.key, { amount: e.target.value })}
                  onKeyDown={(e) => {
                    // Excel-style: Tab or Enter off the last amount starts the
                    // next line.
                    if ((e.key === "Tab" && !e.shiftKey) || e.key === "Enter") {
                      if (isLast && !balanced && row.target) {
                        e.preventDefault();
                        addRow();
                      }
                    }
                  }}
                  placeholder="0.00"
                  type="text"
                  inputMode="decimal"
                  className="h-8 w-28 text-right text-xs tabular-nums"
                  aria-label="ACI allocation amount"
                  aria-invalid={isInvalid || undefined}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={`Remove ACI row ${
                    targetLabelByValue.get(row.target) ?? index + 1
                  }`}
                  onClick={() =>
                    setRows((prev) => prev.filter((r) => r.key !== row.key))
                  }
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between border-t pt-2 text-sm">
        <span className="text-muted-foreground">
          Coded {formatCurrency(sum)}
          {totalAmount !== undefined && ` of ${formatCurrency(totalAmount)}`}
        </span>
        <span className="flex items-center gap-3">
          {rows.length > 0 && delta !== undefined && (
            <span
              className={cn(
                "text-xs font-medium tabular-nums",
                balanced
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-destructive",
              )}
            >
              {balanced
                ? "Balanced"
                : delta > 0
                  ? `${formatCurrency(delta)} over`
                  : `${formatCurrency(-delta)} left to code`}
            </span>
          )}
          <Button size="sm" onClick={handleSave} disabled={isSaving || !dirty}>
            {isSaving ? "Saving…" : "Save ACI coding"}
          </Button>
        </span>
      </div>
    </div>
  );
}
